import { css } from '@emotion/react';
import { DataSourcesTypes } from '@frontend/api-data-sources';
import type { ServiceProvider } from '@frontend/api-service-providers';
import { useTranslation } from '@frontend/i18n';
import { getFullName } from '@frontend/name';
import { theme } from '@frontend/theme';
import { Avatar, Truncated } from '@frontend/design-system';
import { CompactTable, CompactTableRowActionsProps } from '../compact-table';
import { ServiceProviderSourceInfo } from './atoms';
import { ServiceProviderTableProps } from './types';

type Props = ServiceProviderTableProps & {
  tableRowActions: CompactTableRowActionsProps<ServiceProvider>['actions'];
};

export const ServiceProviderCompactTable = ({ providers, isLoading, tableRowActions, integrations }: Props) => {
  return (
    <>
      <CompactTable
        tableId='service-providers-compact-table'
        data={providers ?? []}
        isLoading={isLoading}
        emptyStateConfig={{
          type: 'schedule',
        }}
        rowActions={{
          actions: tableRowActions,
        }}
        rowElement={(provider: ServiceProvider) => <CompactTable.RowHeader title={<RowTitle provider={provider} />} />}
        expandedRowElement={(provider: ServiceProvider) => (
          <ExpandedRowElement
            provider={provider}
            integrations={integrations}
            shouldMapToExternalProviderId={false}
            openModal={() => {}}
          />
        )}
        toolbarElement={
          <div css={styles.toolbar}>
            <CompactTable.ExpandAll />
          </div>
        }
        listHeight={400}
      />
    </>
  );
};

const ExpandedRowElement = ({
  provider,
  integrations,
  shouldMapToExternalProviderId,
  openModal,
}: {
  provider: ServiceProvider;
  integrations: DataSourcesTypes.DataSource[] | undefined;
  shouldMapToExternalProviderId: boolean;
  openModal: () => void;
}) => {
  const { t } = useTranslation('schedule');
  const sourceId = provider.sourceId;
  const emptySourceValue = sourceId && !integrations?.find((integration) => integration.SourceID === sourceId);

  return (
    <CompactTable.ExpandedRow>
      {provider.publicDisplayName && (
        <CompactTable.InfoRow label={t('Display Name')} value={provider.publicDisplayName} />
      )}
      <CompactTable.InfoRow label={t('First Name')} value={provider.firstName} />
      <CompactTable.InfoRow label={t('Last Name')} value={provider.lastName} />
      {!emptySourceValue && (
        <CompactTable.InfoRow
          label={t('Source')}
          value={
            <ServiceProviderSourceInfo
              sourceId={sourceId}
              integrations={integrations}
              shouldMapToExternalProviderId={shouldMapToExternalProviderId}
              openModal={openModal}
            />
          }
        />
      )}
      <CompactTable.InfoRow
        label={t('Updated On')}
        value={provider.updatedAt ? new Date(provider.updatedAt).toLocaleString('en-US') : ''}
      />
    </CompactTable.ExpandedRow>
  );
};

const RowTitle = ({ provider }: { provider: ServiceProvider }) => {
  return (
    <div css={styles.rowTitle}>
      <Avatar name={`${provider.firstName} ${provider.lastName}`} size='small' src={provider.publicDisplayImage} />
      <Truncated css={styles.displayName}>{provider.publicDisplayName || getFullName(provider)}</Truncated>
    </div>
  );
};

const styles = {
  toolbar: css`
    width: 100%;
    display: flex;
    justify-content: flex-end;
  `,
  rowTitle: css`
    display: flex;
    align-items: center;
    max-width: 176px;
    gap: ${theme.spacing(1)};
  `,
  displayName: css`
    max-width: 176px;
    font-weight: ${theme.font.weight.bold};
  `,
};
